export function Features() {
  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Military-Grade Encryption',
      description: 'AES-256 encryption protects your data from hackers, ISPs, and government surveillance, ensuring complete privacy.',
      size: 'large',
      gradient: 'from-indigo-600 to-purple-600'
    },
    {
      icon: 'ri-eye-off-line',
      title: 'Zero-Log Policy',
      description: 'We never track, collect, or share your online activity or personal information.',
      size: 'medium',
      gradient: 'from-white to-gray-50'
    },
    {
      icon: 'ri-global-line',
      title: 'Global Network',
      description: 'Access servers worldwide for optimal speed and unrestricted content access.',
      size: 'medium',
      gradient: 'from-cyan-500 to-blue-600'
    },
    {
      icon: 'ri-shut-down-line',
      title: 'Kill Switch',
      description: 'Automatic protection if VPN connection drops unexpectedly.',
      size: 'small',
      gradient: 'from-gray-100 to-gray-200'
    },
    {
      icon: 'ri-device-line',
      title: 'Multi-Device Support',
      description: 'Protect all your devices with a single subscription - desktop, mobile, and tablet.',
      size: 'tall',
      gradient: 'from-gray-900 to-gray-800'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Expert assistance whenever you need it.',
      size: 'small',
      gradient: 'from-teal-500 to-emerald-600'
    }
  ];

  return (
    <section id="features" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-4">
            <span className="text-sm text-teal-800 font-semibold">Features</span>
          </div>
          <h3 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Complete Privacy Protection
          </h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Advanced security features designed to keep your online activities private and secure
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Large Card - Top Left */}
          <div className={`md:col-span-2 bg-gradient-to-br ${features[0].gradient} rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow`}>
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mb-6">
              <i className={`${features[0].icon} text-4xl text-white`}></i>
            </div>
            <h4 className="text-3xl font-bold text-white mb-4">{features[0].title}</h4>
            <p className="text-lg text-white/90 leading-relaxed">{features[0].description}</p>
          </div>

          {/* Medium Card - Top Right */}
          <div className={`bg-gradient-to-br ${features[1].gradient} rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-200`}>
            <div className="w-12 h-12 flex items-center justify-center bg-indigo-100 rounded-xl mb-4">
              <i className={`${features[1].icon} text-2xl text-indigo-600`}></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-3">{features[1].title}</h4>
            <p className="text-gray-600 leading-relaxed">{features[1].description}</p>
          </div>

          {/* Medium Card - Bottom Left */}
          <div className={`bg-gradient-to-br ${features[2].gradient} rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow`}>
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-4">
              <i className={`${features[2].icon} text-2xl text-white`}></i>
            </div>
            <h4 className="text-2xl font-bold text-white mb-3">{features[2].title}</h4>
            <p className="text-white/90 leading-relaxed">{features[2].description}</p>
          </div>

          {/* Small Card - Bottom Middle */}
          <div className={`bg-gradient-to-br ${features[3].gradient} rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow`}>
            <div className="w-12 h-12 flex items-center justify-center bg-gray-300 rounded-xl mb-4">
              <i className={`${features[3].icon} text-2xl text-gray-700`}></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-3">{features[3].title}</h4>
            <p className="text-gray-600 leading-relaxed">{features[3].description}</p>
          </div>

          {/* Tall Card - Bottom Right (spans 2 rows) */}
          <div className={`md:row-span-2 bg-gradient-to-br ${features[4].gradient} rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow`}>
            <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl mb-4">
              <i className={`${features[4].icon} text-2xl text-white`}></i>
            </div>
            <h4 className="text-2xl font-bold text-white mb-3">{features[4].title}</h4>
            <p className="text-white/80 leading-relaxed mb-6">{features[4].description}</p>
            <div className="mt-auto pt-8">
              <div className="flex items-center gap-3 text-teal-400">
                <i className="ri-checkbox-circle-fill text-xl"></i>
                <span className="text-sm">Windows, macOS, Linux</span>
              </div>
              <div className="flex items-center gap-3 text-teal-400 mt-2">
                <i className="ri-checkbox-circle-fill text-xl"></i>
                <span className="text-sm">iOS & Android</span>
              </div>
            </div>
          </div>

          {/* Small Card - Support */}
          <div className={`md:col-span-2 bg-gradient-to-br ${features[5].gradient} rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow`}>
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-4">
              <i className={`${features[5].icon} text-2xl text-white`}></i>
            </div>
            <h4 className="text-2xl font-bold text-white mb-3">{features[5].title}</h4>
            <p className="text-white/90 leading-relaxed">{features[5].description}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
